import { Box, Tabs, Tab, Typography } from '@mui/material';
import Layout from '../../../../../components/Layout';
import AdminNavbar from '../../../../../components/Admin/AdminNavbar/AdminNavbar';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import PayoutOverview from '../../../../../components/Admin/Payments/Payout/PayoutOverview/PayoutOverview';
import PayoutDetail from '../../../../../components/Admin/Payments/Payout/PayoutDetail/IndividualPayoutDetail';
import './PayoutDetails.css';
import { ArrowDownward } from '@mui/icons-material';
import toast from 'react-hot-toast';

const StatusColor = {
  Pending: { color: '#E0AD14', bgcolor: '#FFFCF2', value: 'Pending' },
  Failed: { color: '#E14949', bgcolor: '#FFF2F2', value: 'Failed' },
  Success: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Success' },
};

const PayoutDetails = () => {
  const [tab, setTab] = useState(0);
  const { id: payoutId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const teacherId = state?.teacherId;
  const [isLoading, setIsLoading] = useState(false);
  const [teacher, setTeacher] = useState(null);
  const [payout, setPayout] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [rbacMe, setRbacMe] = useState(null);

  // Load current user permissions
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);

        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const { data: payoutData } = await axiosInstance.get(
          `/admin/transactions/payout-request/${payoutId}`
        );
        setPayout(payoutData);
        const resolvedTeacherId = payoutData?.teacher_id || teacherId;
        if (!resolvedTeacherId) {
          setIsLoading(false);
          return;
        }
        const [teacherRes, bankRes] = await Promise.all([
          axiosInstance.get(`/admin/teachers/${resolvedTeacherId}`),
          axiosInstance.get(`/admin/bank_details/${resolvedTeacherId}`),
        ]);
        const teacherData = teacherRes?.data || {};
        const bankData = bankRes?.data || {};

        const mappedTeacher = {
          id: resolvedTeacherId,
          name: `${teacherData.first_name || ''}${teacherData.last_name ? ' ' + teacherData.last_name : ''}`.trim(),
          email: teacherData.email || '',
          phone: teacherData.phone_no || '',
          address: bankData.address || '',
          city: teacherData.city || '',
          country: teacherData.current_location || '',
          totalEarning: payoutData?.wallet_details?.total_earnings || 0,
          lockedEarning: payoutData?.wallet_details?.locked_earnings || 0,
          totalWithdrawal: payoutData?.wallet_details?.total_payout || 0,
          requestedAmount: payoutData?.total_amount || 0,
          bankName: bankData.bank_name || '',
          bankStatement: bankData.statement_url || '',
          iban: bankData.IBAN_no || '',
        };
        setTeacher(mappedTeacher);
      } catch (e) {
        // no-op UI change; silently fail or handle via global toast if needed
      } finally {
        setIsLoading(false);
      }
    };
    if (payoutId) {
      fetchData();
    }
  }, [payoutId, teacherId]);

  const statusDisplay = React.useMemo(() => {
    const raw = payout?.status || '';
    const norm = typeof raw === 'string' ? raw.toLowerCase() : '';
    if (norm === 'success') return 'Success';
    if (norm === 'failed') return 'Failed';
    if (norm === 'pending') return 'Pending';
    return '';
  }, [payout?.status]);

  const statusStyle = React.useMemo(() => {
    const conf = StatusColor[statusDisplay];
    if (!conf) return {};
    return { color: conf.color, backgroundColor: conf.bgcolor, border: `1px solid ${conf.color}` };
  }, [statusDisplay]);

  // Helper function to check if user has a specific permission
  const hasPermission = permission => {
    if (!rbacMe || !Array.isArray(rbacMe.permissions)) return false;
    return rbacMe.permissions.includes(permission);
  };

  // Check if user can download payout reports
  const canDownloadPayout = () => {
    return hasPermission('download_bank_transaction_report');
  };

  const handleDownload = async () => {
    if (isDownloading) return;

    // Check permission first
    if (!canDownloadPayout()) {
      toast.dismiss();
      toast.error(
        "You don't have permission to download payout reports. Please contact your administrator."
      );
      return;
    }

    const id = teacher?.id;
    if (!id) {
      toast.error('Teacher not found');
      return;
    }
    const toastId = toast.loading('Preparing download...');
    try {
      setIsDownloading(true);
      const { data } = await axiosInstance.post(
        '/admin/transactions/download-pending-payout-requests',
        { teacher_ids: [id] }
      );
      const url = data?.url;
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Download started');
      } else {
        toast.error('No download URL returned');
      }
    } catch (e) {
      toast.error('Failed to start download');
    } finally {
      toast.dismiss(toastId);
      setIsDownloading(false);
    }
  };

  const handleViewProfile = () => {
    const id = teacher?.id;
    if (!id) return;
    navigate(`/admin/users/instructor-details/${id}`);
  };
  return (
    <Layout title="Klass Online | Payout Details">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="payoutd-container">
          {/* Header */}
          <Box className="payoutd-header">
            <Box className="payoutd-header-left">
              <Box className="payoutd-breadcrumb">
                Payment {'>'} Payout {'>'}{' '}
                <span className="payoutd-breadcrumb-current">Details</span>
              </Box>
              <Box className="payoutd-name-row">
                <span className="payoutd-name">{teacher?.name || ''}</span>
                {statusDisplay ? (
                  <span className="payoutd-status-pill" style={statusStyle}>
                    {statusDisplay}
                  </span>
                ) : null}
              </Box>
              {/* <Box className="payoutd-breadcrumb" style={{ marginTop: 4 }}>
                Payout ID: {payoutId} {teacherId ? `| Teacher ID: ${teacherId}` : ''}
              </Box> */}
            </Box>
            <Box className="payoutd-header-right">
              <button
                type="button"
                className="payoutd-view-link"
                onClick={handleViewProfile}
                disabled={!teacher?.id}
              >
                View Details
              </button>
              {tab === 0 && (
                <button
                  type="button"
                  className="payoutd-download-btn"
                  onClick={handleDownload}
                  disabled={isDownloading}
                >
                  <ArrowDownward className="payoutd-download-btn-icon" />
                  <Typography className="payoutd-download-btn-text">
                    {isDownloading ? 'Preparing...' : 'Download'}
                  </Typography>
                </button>
              )}
            </Box>
          </Box>

          {/* Card with Tabs + Content */}
          <Box className="payoutd-card">
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              aria-label="payout details tabs"
              className="payoutd-tabs"
            >
              <Tab label="Overview" />
              <Tab label="Payout" />
            </Tabs>
            <Box className="payoutd-card-body">
              {tab === 0 ? (
                <PayoutOverview teacher={teacher} />
              ) : (
                <PayoutDetail teacher={teacher} payout={payout} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default PayoutDetails;
